"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

// Player do hit fixo no canto. Navegadores bloqueiam som automático, então ele
// só toca depois de um clique (no botão do canto ou em qualquer <PlayHitButton/>).
// Começa no refrão, toca até o fim e repete a música inteira em loop. Toda visita
// começa com o volume em `volume` (60%); o controle deslizante do selo deixa a
// pessoa aumentar, abaixar ou silenciar. Volume e fade-in passam por um GainNode
// do Web Audio — o iOS ignora audio.volume, mas respeita o ganho. Pausa sozinho quando
// um clipe ou a playlist do Spotify começam (evento "mf:pause-player").

export type PlayerProps = {
  titulo: string;
  artistas: string;
  audio: string;
  inicio: number;
  fade: number;
  volume: number;
  capa: string | null;
};

const EVT_TOGGLE = "mf:toggle-player";
const EVT_PAUSE = "mf:pause-player";

/** Dispara em qualquer lugar do site para tocar/pausar o hit. */
export function togglePlayer() {
  window.dispatchEvent(new Event(EVT_TOGGLE));
}

/** Os outros players (YouTube, Spotify) chamam isso ao começar. */
export function pausePlayer() {
  window.dispatchEvent(new Event(EVT_PAUSE));
}

function track(label: string) {
  const body = JSON.stringify({ t: "play", l: label, p: location.pathname, r: document.referrer });
  navigator.sendBeacon?.("/api/track", new Blob([body], { type: "application/json" }));
}

export function MiniPlayer({ titulo, artistas, audio, inicio, fade, volume, capa }: PlayerProps) {
  const el = useRef<HTMLAudioElement>(null);
  const graph = useRef<{ ctx: AudioContext; gain: GainNode } | null>(null);
  const started = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [vol, setVol] = useState(volume);
  const [muted, setMuted] = useState(false);
  // Volume efetivo lido dentro dos callbacks de áudio
  const level = useRef(volume);
  useEffect(() => {
    level.current = muted ? 0 : vol;
  }, [muted, vol]);

  /** Aplica o volume escolhido (cancela um fade-in em andamento). */
  const applyLevel = useCallback((v: number) => {
    const g = graph.current;
    if (g) {
      const t = g.ctx.currentTime;
      g.gain.gain.cancelScheduledValues(t);
      g.gain.gain.setTargetAtTime(v, t, 0.04);
    } else if (el.current) {
      el.current.volume = v;
    }
  }, []);

  const pause = useCallback(() => {
    const a = el.current;
    if (!a || a.paused) return;
    const g = graph.current;
    if (g) {
      // fade-out curtinho para não "cortar" o som
      const t = g.ctx.currentTime;
      g.gain.gain.cancelScheduledValues(t);
      g.gain.gain.setValueAtTime(g.gain.gain.value, t);
      g.gain.gain.linearRampToValueAtTime(0, t + 0.35);
      setTimeout(() => a.pause(), 360);
    } else {
      a.pause();
    }
  }, []);

  const play = useCallback(async () => {
    const a = el.current;
    if (!a) return;
    // O grafo precisa nascer dentro do gesto do usuário (política de autoplay)
    if (!graph.current) {
      try {
        const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new Ctx();
        const gain = ctx.createGain();
        ctx.createMediaElementSource(a).connect(gain).connect(ctx.destination);
        graph.current = { ctx, gain };
      } catch {
        graph.current = null; // sem Web Audio: toca sem fade, só com o volume do elemento
        a.volume = level.current;
      }
    }
    const g = graph.current;
    if (g?.ctx.state === "suspended") await g.ctx.resume();

    const first = !started.current;
    if (first) {
      started.current = true;
      a.currentTime = inicio;
      track(`Player: ${titulo}`);
    }
    if (g) {
      const t = g.ctx.currentTime;
      g.gain.gain.cancelScheduledValues(t);
      g.gain.gain.setValueAtTime(0.0001, t);
      g.gain.gain.linearRampToValueAtTime(Math.max(0.0001, level.current), t + (first ? fade : 0.4));
    }
    pausePlayerOthers();
    await a.play().catch(() => {});
  }, [inicio, fade, titulo]);

  const toggle = useCallback(() => {
    if (el.current?.paused) void play();
    else pause();
  }, [play, pause]);

  useEffect(() => {
    window.addEventListener(EVT_TOGGLE, toggle);
    window.addEventListener(EVT_PAUSE, pause);
    return () => {
      window.removeEventListener(EVT_TOGGLE, toggle);
      window.removeEventListener(EVT_PAUSE, pause);
    };
  }, [toggle, pause]);

  // Controles na tela de bloqueio / central de mídia do celular
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: titulo,
      artist: artistas,
      album: "Mariana Fagundes",
      artwork: capa ? [{ src: capa, sizes: "300x300", type: "image/jpeg" }] : [],
    });
    navigator.mediaSession.setActionHandler("play", () => void play());
    navigator.mediaSession.setActionHandler("pause", pause);
  }, [titulo, artistas, capa, play, pause]);

  return (
    <div className="fixed bottom-4 left-4 z-40 md:bottom-6 md:left-6">
      <audio
        ref={el}
        src={audio}
        preload="none"
        loop
        crossOrigin="anonymous"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => {
          const a = e.currentTarget;
          if (a.duration) setProgress(a.currentTime / a.duration);
        }}
      />
      <div className="relative flex items-center gap-1 overflow-hidden rounded-full border border-champanhe/25 bg-noite/85 py-2 pl-2 pr-4 shadow-[0_10px_40px_-10px_rgba(192,20,47,0.6)] backdrop-blur-xl transition hover:border-champanhe/60">
        {/* progresso da música na borda de baixo */}
        <span aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 bg-champanhe/10">
          <span className="block h-full bg-rubi transition-[width] duration-300" style={{ width: `${progress * 100}%` }} />
        </span>
        <button
          type="button"
          onClick={toggle}
          aria-pressed={playing}
          className="flex items-center gap-3 rounded-full pr-2 text-left"
        >
          {/* nome acessível = ação + texto visível (regra label-content-name-mismatch) */}
          <span className="sr-only">{playing ? "Pausar" : "Tocar"}</span>
          <span className="relative grid size-11 shrink-0 place-items-center overflow-hidden rounded-full bg-bordo">
            {capa && (
              <Image src={capa} alt="" width={44} height={44} className={`absolute inset-0 size-full object-cover ${playing ? "animate-[spin_8s_linear_infinite]" : ""}`} />
            )}
            <span className="relative grid size-7 place-items-center rounded-full bg-noite/70 text-champanhe">
              {playing ? (
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
                  <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden className="translate-x-px">
                  <path d="M8 5.1v13.8a.6.6 0 0 0 .9.5l10.8-6.9a.6.6 0 0 0 0-1L8.9 4.6a.6.6 0 0 0-.9.5z" />
                </svg>
              )}
            </span>
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.3em] text-champanhe/70">
              {playing ? (
                <span aria-hidden className="flex h-2.5 items-end gap-[2px]">
                  {[0, 1, 2, 3].map((i) => (
                    <span key={i} className="eq w-[2px] rounded-full bg-rubi" style={{ animationDelay: `${i * -0.2}s` }} />
                  ))}
                </span>
              ) : null}
              {playing ? "Tocando" : "Ouça o hit"}
            </span>
            <span className="block max-w-[8.5rem] truncate font-display text-lg uppercase leading-tight text-areia sm:max-w-[11rem]">{titulo}</span>
          </span>
        </button>

        {/* Volume: começa em 60% a cada visita; a pessoa ajusta como quiser */}
        <div className="flex items-center gap-2 border-l border-champanhe/15 pl-3">
          <button
            type="button"
            onClick={() => {
              const next = !muted;
              setMuted(next);
              applyLevel(next ? 0 : vol);
            }}
            aria-label={muted ? "Ativar som" : "Silenciar"}
            aria-pressed={muted}
            className="text-champanhe/80 transition hover:text-champanhe"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
              <path d="M4 9v6h4l5 4V5L8 9H4z" />
              {muted || vol === 0 ? (
                <path d="m16 9 5 6m0-6-5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
              ) : (
                <path
                  d={vol > 0.5 ? "M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" : "M16 8.5a5 5 0 0 1 0 7"}
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  fill="none"
                />
              )}
            </svg>
          </button>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round((muted ? 0 : vol) * 100)}
            onChange={(e) => {
              const v = Number(e.target.value) / 100;
              setVol(v);
              setMuted(false);
              applyLevel(v);
            }}
            aria-label="Volume"
            aria-valuetext={`${Math.round((muted ? 0 : vol) * 100)}%`}
            className="h-1 w-16 cursor-pointer accent-rubi sm:w-24"
          />
        </div>
      </div>
    </div>
  );
}

// Avisa os outros players (clipes/Spotify) — eles ouvem via Facades
function pausePlayerOthers() {
  window.dispatchEvent(new Event("mf:pause-others"));
}
